// Iter159 — MarkovLab paused-user takeover.
// Shown when the 15-day trial has ended and no active Stripe subscription
// is in place. Full page block, but read-only Portfolio tab remains
// accessible (per user spec — losing sight of one's positions feels
// punitive).
import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export default function TrialEndedPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [demoMode, setDemoMode] = React.useState(false);

  React.useEffect(() => {
    api.get("/billing/me")
      .then(({ data }) => setDemoMode(!!data?.demo_mode))
      .catch(() => {});
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin: window.location.origin,
        package: "markovlab_monthly",
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (msg?.code === "not_configured") {
        toast.error("Stripe not yet configured — check back shortly.");
      } else {
        toast.error("Checkout failed to start.");
      }
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "var(--hoip-bg)" }}
      data-testid="trial-ended-page"
    >
      <div
        className="max-w-lg border rounded-sm p-8"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      >
        <Lock className="w-8 h-8 mb-4 text-amber-400" />
        <h1
          className="text-2xl font-bold mb-2"
          style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}
          data-testid="trial-ended-title"
        >
          Your 15-day trial ended
        </h1>
        <p className="text-sm mb-6" style={{ color: "var(--hoip-text-secondary)" }}>
          {demoMode ? (
            <>MarkovLab is in demo mode — paid subscriptions aren&apos;t open yet.
              Email us for early access; your positions and trade history are safe
              and preserved.</>
          ) : (
            <>Subscribe to keep full access to the Scanner, Wheel &amp; Other
              Strategies, and the Portfolio Intelligence Engine. Your positions
              and trade history are safe and preserved &mdash; nothing gets deleted.</>
          )}
        </p>

        {demoMode ? (
          <a href="mailto:info@markovlab.app?subject=MarkovLab%20early%20access"
            className="block w-full mb-3 text-center py-2.5 rounded-md font-semibold"
            style={{ background: "var(--hoip-profit)", color: "#000" }}
            data-testid="trial-ended-contact-btn">
            Contact us for early access
          </a>
        ) : (
          <Button
            size="lg"
            className="w-full mb-3"
            disabled={loading}
            onClick={subscribe}
            data-testid="trial-ended-subscribe-btn"
          >
            {loading ? "Starting checkout…" : "Subscribe now"}
          </Button>
        )}

        <Button
          size="sm"
          variant="outline"
          className="w-full text-xs"
          onClick={() => navigate("/portfolio?readonly=1")}
          data-testid="trial-ended-view-portfolio-btn"
        >
          <Eye className="w-3 h-3 mr-1" />
          View my portfolio (read-only)
        </Button>

        <div className="mt-6 pt-4 border-t text-[10px]"
          style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>
          Everything else on the app is paused until you subscribe. Your
          data (positions, trades, saved baskets) stays intact indefinitely.
        </div>
      </div>
    </div>
  );
}
